// §9.3 / §11.2 — `exec` delegation: an installed app becomes a command provider.
//
// The whole feature in one sentence: the terminal types a command it does not know, the kernel
// finds the app whose manifest claims it, sends `exec/run` down that app's bridge, and streams the
// frames back. **Hangar never executes code server-side** (§11.2) — with Flightdeck installed,
// `git` runs in Flightdeck's container and the frames are what crosses the boundary.
//
// Two things this must get right, and both are about *not trusting the app*:
//
//   • **A command runs only if the app declared it and holds `exec.provide`.** A manifest that
//     lists commands without the permission provides nothing; asking the app what it supports at
//     run time would let it claim anything after the user had approved something narrower.
//   • **A stream that never ends must still end.** An app that accepts `exec/run` and then goes
//     quiet would leave a terminal tab wedged forever, so the run has a timeout and the timeout
//     produces an ordinary non-zero exit, which is a thing a shell already knows how to show.
import type { ExecFrame } from '@hangar/shared';

/** How long a command may go without producing a frame before the kernel ends it. */
export const EXEC_IDLE_TIMEOUT_MS = 30_000;

/** What the exec service needs from the rest of the kernel. */
export interface ExecServiceDeps {
  /**
   * The installed apps that hold `exec.provide`, **in install order** (§11.2), each with the
   * commands its manifest declares.
   */
  readonly providers: () => readonly ExecProviderApp[];
  /**
   * Send `exec/run` to a live instance of `appId`, and deliver its frames.
   *
   * Answers `null` when the app has no bridged window — a provider that is installed but not
   * running cannot be asked, and pretending otherwise would hang the terminal.
   */
  readonly send: (
    appId: string,
    request: ExecRequest,
    onFrame: (frame: ExecFrame) => void,
  ) => (() => void) | null;
  readonly now?: () => number;
}

export interface ExecProviderApp {
  readonly appId: string;
  readonly name: string;
  readonly commands: readonly string[];
}

export interface ExecRequest {
  readonly cmd: string;
  readonly args: readonly string[];
  readonly cwd: string;
}

export interface ExecService {
  /** Which app would answer `cmd`, or `null`. Install order, first match (§11.2). */
  resolve(cmd: string): ExecProviderApp | null;
  /** Every command any installed provider claims — what the terminal offers for completion. */
  commands(): readonly string[];
  /** The providers themselves, in install order, for the terminal's resolution list (§11.2). */
  commandsByApp(): readonly ExecProviderApp[];
  /** Run `cmd` in its provider, yielding frames as they arrive. */
  run(request: ExecRequest): AsyncIterable<ExecFrame>;
}

export function createExecService(deps: ExecServiceDeps): ExecService {
  return {
    resolve(cmd) {
      return deps.providers().find((provider) => provider.commands.includes(cmd)) ?? null;
    },

    commands() {
      return [...new Set(deps.providers().flatMap((provider) => [...provider.commands]))];
    },

    commandsByApp() {
      return deps.providers();
    },

    async *run(request): AsyncIterable<ExecFrame> {
      const provider = this.resolve(request.cmd);
      if (!provider) {
        // The terminal prints §11.2's not-found string; this is the same condition reaching the
        // kernel, and it is not an error worth throwing over.
        yield { kind: 'exit', code: 127 };
        return;
      }

      // A queue, because frames arrive on the bridge's schedule and are consumed on the
      // terminal's. Without one, a burst of output between two `await`s would be dropped.
      const pending: ExecFrame[] = [];
      let notify: (() => void) | null = null;
      let finished = false;
      let timedOut = false;

      const push = (frame: ExecFrame) => {
        pending.push(frame);
        if (frame.kind === 'exit') finished = true;
        notify?.();
      };

      const detach = deps.send(provider.appId, request, push);
      if (!detach) {
        // Installed but not running, or its frame never bridged (§18 F7).
        yield { kind: 'stderr', text: `${request.cmd}: ${provider.name} is not running` };
        yield { kind: 'exit', code: 127 };
        return;
      }

      let timer: ReturnType<typeof setTimeout> | null = null;
      const arm = () => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          timedOut = true;
          finished = true;
          notify?.();
        }, EXEC_IDLE_TIMEOUT_MS);
      };

      try {
        arm();
        for (;;) {
          while (pending.length > 0) {
            const frame = pending.shift();
            if (!frame) break;
            arm();
            yield frame;
            if (frame.kind === 'exit') return;
          }
          if (timedOut) {
            yield { kind: 'stderr', text: `${request.cmd}: ${provider.name} stopped responding` };
            yield { kind: 'exit', code: 124 };
            return;
          }
          if (finished) return;
          await new Promise<void>((resolve) => {
            notify = () => {
              notify = null;
              resolve();
            };
          });
        }
      } finally {
        if (timer) clearTimeout(timer);
        detach();
      }
    },
  };
}
